import request from './request'
import type { CareScheduleAssignTargetType } from './CareScheduleAssign'

/** POST `/api/admin/care-schedule/availability` 请求体（raw-json） */
export interface CareScheduleAvailabilityRequest {
  technician_id: number
  /** 查询区间起始（周日首格）`YYYY-MM-DD HH:mm` */
  time_start: string
  /** 查询区间结束（周六末格）`YYYY-MM-DD HH:mm` */
  time_end: string
  target_type: CareScheduleAssignTargetType
  target_id: number
}

export interface CareScheduleAvailabilityResponse {
  code: number
  msg: string
  data?: unknown
}

export type CareScheduleDayAvailabilityTone = 'red' | 'blue' | 'green'

/** 解析后的检查员周可用性（按日） */
export interface ParsedCareScheduleAvailability {
  weekStart?: string
  /** 不可选时段（占用 / 不可用），键为 YYYY-MM-DD */
  blockedSlotsByDate: Record<string, string[]>
  /** 可选时段白名单（若后端只返回 available） */
  availableSlotsByDate: Record<string, string[]>
  /** 日期条颜色：红满 / 蓝部分 / 绿空闲 */
  dayStatusByDate: Record<string, CareScheduleDayAvailabilityTone>
}

function readYmd(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const m = raw.trim().match(/(\d{4}-\d{2}-\d{2})/)
  return m?.[1] ?? null
}

function readSlotList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  return raw.map((x) => String(x ?? '').trim()).filter(Boolean)
}

function mergeSlotList(
  target: Record<string, string[]>,
  dateKey: string,
  slots: string[],
): void {
  if (!slots.length) return
  const prev = target[dateKey] ?? []
  target[dateKey] = [...new Set([...prev, ...slots])]
}

function readTone(raw: unknown): CareScheduleDayAvailabilityTone | null {
  if (typeof raw !== 'string') return null
  const s = raw.trim().toLowerCase()
  if (s === 'red' || s === 'full' || s === 'unavailable' || s === 'disabled') return 'red'
  if (s === 'blue' || s === 'partial' || s === 'busy') return 'blue'
  if (s === 'green' || s === 'free' || s === 'available') return 'green'
  if (s.includes('bar-red') || s === 'bar_red') return 'red'
  if (s.includes('bar-blue') || s === 'bar_blue') return 'blue'
  if (s.includes('bar-green') || s === 'bar_green') return 'green'
  return null
}

/** 从 context / availability 等 payload 解析日期条颜色（week_dates、day_status 等） */
export function readDayStatusByDateFromSchedulePayload(
  data: Record<string, unknown>,
): Record<string, CareScheduleDayAvailabilityTone> {
  const dayStatusByDate: Record<string, CareScheduleDayAvailabilityTone> = {}
  const blockedSlotsByDate: Record<string, string[]> = {}
  const availableSlotsByDate: Record<string, string[]> = {}

  const dayArrays = [
    data.week_dates,
    data.weekDates,
    data.days,
    data.week_days,
    data.week,
    data.dates,
  ]
  for (const arr of dayArrays) {
    if (!Array.isArray(arr)) continue
    for (const item of arr) {
      if (item && typeof item === 'object') {
        parseDayEntry(
          item as Record<string, unknown>,
          blockedSlotsByDate,
          availableSlotsByDate,
          dayStatusByDate,
        )
      }
    }
  }

  const toneMaps = [data.day_status, data.day_tones, data.date_status]
  for (const m of toneMaps) {
    if (!m || typeof m !== 'object' || Array.isArray(m)) continue
    for (const [key, val] of Object.entries(m as Record<string, unknown>)) {
      const ymd = readYmd(key) ?? key.match(/(\d{4}-\d{2}-\d{2})/)?.[1]
      const tone = readTone(val)
      if (ymd && tone) dayStatusByDate[ymd] = tone
    }
  }

  return dayStatusByDate
}

function parseDayEntry(
  entry: Record<string, unknown>,
  blocked: Record<string, string[]>,
  available: Record<string, string[]>,
  tones: Record<string, CareScheduleDayAvailabilityTone>,
): void {
  const ymd =
    readYmd(entry.date) ??
    readYmd(entry.service_date) ??
    readYmd(entry.day) ??
    readYmd(entry.ymd)
  if (!ymd) return

  const blockedSlots = readSlotList(
    entry.blocked_slots ??
      entry.unavailable_slots ??
      entry.disabled_slots ??
      entry.occupied_slots,
  )
  const availableSlots = readSlotList(
    entry.available_slots ?? entry.slots ?? entry.free_slots,
  )
  if (blockedSlots.length) mergeSlotList(blocked, ymd, blockedSlots)
  if (availableSlots.length) mergeSlotList(available, ymd, availableSlots)

  const tone = readTone(
    entry.status ??
      entry.tone ??
      entry.availability ??
      entry.color ??
      entry.bar_class ??
      entry.barClass,
  )
  if (tone) {
    tones[ymd] = tone
    return
  }
  if (entry.selectable === false || entry.is_selectable === false) {
    tones[ymd] = 'red'
  }
}

function parseSlotsArray(
  rows: unknown[],
  blocked: Record<string, string[]>,
  available: Record<string, string[]>,
): void {
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue
    const o = row as Record<string, unknown>
    const ymd = readYmd(o.date) ?? readYmd(o.service_date)
    if (!ymd) continue
    const slot = String(o.slot ?? o.time ?? o.time_start ?? o.label ?? '').trim()
    if (!slot) continue
    const availableFlag = o.available ?? o.is_available ?? o.free
    if (availableFlag === false || availableFlag === 0 || availableFlag === '0') {
      mergeSlotList(blocked, ymd, [slot])
    } else if (availableFlag === true || availableFlag === 1 || availableFlag === '1') {
      mergeSlotList(available, ymd, [slot])
    } else {
      mergeSlotList(blocked, ymd, [slot])
    }
  }
}

function parseDateMap(
  map: Record<string, unknown>,
  target: Record<string, string[]>,
): void {
  for (const [key, val] of Object.entries(map)) {
    const ymd = readYmd(key) ?? key.match(/(\d{4}-\d{2}-\d{2})/)?.[1]
    if (!ymd) continue
    mergeSlotList(target, ymd, readSlotList(val))
  }
}

/** 解析 availability 接口 `data` */
export function parseCareScheduleAvailabilityResponse(
  raw: unknown,
): ParsedCareScheduleAvailability | null {
  if (raw == null || typeof raw !== 'object') return null
  const r = raw as CareScheduleAvailabilityResponse
  if (r.code !== 200 || r.data == null || typeof r.data !== 'object') return null

  const data = r.data as Record<string, unknown>
  const blockedSlotsByDate: Record<string, string[]> = {}
  const availableSlotsByDate: Record<string, string[]> = {}
  const dayStatusByDate = readDayStatusByDateFromSchedulePayload(data)

  const weekStart =
    readYmd(data.time_start) ??
    readYmd(data.week_start) ??
    readYmd(data.weekStart) ??
    undefined

  const dayArrays = [data.days, data.week_days, data.week, data.dates]
  for (const arr of dayArrays) {
    if (!Array.isArray(arr)) continue
    for (const item of arr) {
      if (item && typeof item === 'object') {
        parseDayEntry(item as Record<string, unknown>, blockedSlotsByDate, availableSlotsByDate, {})
      }
    }
  }

  const slotRows = data.slots ?? data.time_slots ?? data.availability_slots
  if (Array.isArray(slotRows)) parseSlotsArray(slotRows, blockedSlotsByDate, availableSlotsByDate)

  const blockedMaps = [
    data.blocked_slots_by_date,
    data.unavailable_slots,
    data.disabled_slots,
    data.occupied_slots,
  ]
  for (const m of blockedMaps) {
    if (m && typeof m === 'object' && !Array.isArray(m)) {
      parseDateMap(m as Record<string, unknown>, blockedSlotsByDate)
    }
  }

  const availableMaps = [data.available_slots, data.available_slots_by_date, data.free_slots]
  for (const m of availableMaps) {
    if (m && typeof m === 'object' && !Array.isArray(m)) {
      parseDateMap(m as Record<string, unknown>, availableSlotsByDate)
    }
  }

  return {
    weekStart,
    blockedSlotsByDate,
    availableSlotsByDate,
    dayStatusByDate,
  }
}

export async function postCareScheduleAvailability(
  body: CareScheduleAvailabilityRequest,
): Promise<ParsedCareScheduleAvailability> {
  const raw = await request.post<CareScheduleAvailabilityResponse>(
    '/api/admin/care-schedule/availability',
    body,
  )
  const parsed = parseCareScheduleAvailabilityResponse(raw)
  if (parsed) return parsed
  const msg =
    typeof raw?.msg === 'string' && raw.msg.trim() ? raw.msg : 'Failed to load inspector availability'
  throw new Error(msg)
}
